/**
 * Client cabinet language.
 *
 * The cabinet was written in Ukrainian end to end, which is right for most of
 * the book and wrong for the rest: TRP partners and the Kazakhstan accounts open
 * their own portal and read a page they cannot read. backlog 87c81e60 / 6f47ee8f.
 *
 * The key IS the Ukrainian source string. A missing translation therefore falls
 * back to Ukrainian instead of showing a key — the page stays usable while the
 * dictionary catches up, and adding a string costs one line here.
 */
import React, { createContext, useContext, useMemo } from "react";

export const CABINET_LANGUAGES = [
  { code: "uk", label: "Українська" },
  { code: "en", label: "English" },
];

const EN = {
  // ── shell / navigation ────────────────────────────────────────────────
  "Огляд": "Overview",
  "Задачі": "Tasks",
  "Оплати": "Payments",
  "Мої запити": "My requests",
  "Вийти": "Sign out",
  "Клієнт": "Client",
  "Розділи": "Sections",
  "Кабінет клієнта": "Client cabinet",
  "Перегляд як клієнт": "Viewing as client",
  "режим тільки для перегляду (read-only)": "read-only preview",

  // ── project picker / status ───────────────────────────────────────────
  "Активні": "Active",
  "Завершені": "Completed",
  "Пошук проєкту": "Search projects",
  "Ваші проєкти": "Your projects",
  "активний": "active",
  "активні": "active",
  "активних": "active",
  "Оберіть проєкт, щоб побачити його задачі та оплати": "Pick a project to see its tasks and payments",
  "В роботі": "In progress",
  "Готуємо пропозицію": "Preparing the proposal",
  "Завершено": "Completed",
  "Запускаємо роботу": "Starting the work",
  "Погоджуємо договір": "Agreeing the contract",
  "Чекаємо на вас": "Waiting for you",
  "Чекаємо передоплату": "Waiting for the prepayment",
  "Проєкт": "Project",
  "Інше": "Other",
  "3D-візуалізація": "3D visualisation",
  "Вебсайт": "Website",
  "3D-візуалізація · Вебсайт": "3D visualisation · Website",
  "360°-тури": "360° tours",
  "3D-модель": "3D model",

  // ── dates / plurals ───────────────────────────────────────────────────
  "день": "day",
  "дні": "days",
  "днів": "days",
  "сьогодні": "today",
  "Дату уточнюємо": "Date to be confirmed",
  "минула": "passed",
  "січень": "January",
  "лютий": "February",
  "березень": "March",
  "квітень": "April",
  "травень": "May",
  "червень": "June",
  "липень": "July",
  "серпень": "August",
  "вересень": "September",
  "жовтень": "October",
  "листопад": "November",
  "грудень": "December",

  // ── dashboard ─────────────────────────────────────────────────────────
  "Доброго ранку": "Good morning",
  "Доброго дня": "Good afternoon",
  "Доброго вечора": "Good evening",
  "Потрібна ваша увага": "Needs your attention",
  "Від вас нічого не потрібно": "Nothing needed from you",
  "Ми напишемо, коли знадобиться ваша відповідь або оплата.":
    "We will write when we need your answer or a payment.",
  "Сповіщення": "Notifications",
  "Ваша команда": "Your team",
  "На зв'язку пн–пт, 9:00–19:00": "Available Mon–Fri, 9:00–19:00",
  "Менеджер проєкту": "Project manager",
  "Менеджер з продажу": "Sales manager",
  "Головний контакт": "Main contact",
  "Пишіть щодо:": "Write about:",
  "хід робіт, правки, терміни": "progress, revisions, deadlines",
  "договір, рахунки, нові задачі": "contract, invoices, new tasks",
  "Скопіювати": "Copy",
  "Номер скопійовано": "Number copied",
  "Задачі проєкту": "Project tasks",
  "Задачі з’являться тут, щойно почнеться робота.": "Tasks will appear here as soon as work starts.",
  "Проєктів поки немає. Щойно ми почнемо роботу, він з’явиться тут.":
    "No projects yet. One will appear here as soon as we start.",
  "Терміни проєкту": "Project timeline",
  "день до здачі": "day to delivery",
  "дні до здачі": "days to delivery",
  "днів до здачі": "days to delivery",
  "часу минуло": "of the time elapsed",
  "днів проєкту": "days of the project",
  "Старт": "Start",
  "Здача": "Delivery",
  "Сьогодні": "Today",
  "Що ми для вас робимо": "What we are doing for you",
  "Результат": "Result",
  "Усі платежі, рахунки й акти": "All payments, invoices and acts",
  "Розглянути": "Review",
  "Відкрити": "Open",
  "Відкрити рахунок": "Open invoice",
  "Реквізити": "Bank details",
  "Рахунок до оплати до": "Invoice due by",
  "усі запити": "all requests",
  "І ще": "And",
  "Заповніть бриф проєкту": "Fill in the project brief",
  "Зміна в задачі": "Change in a task",

  // ── tasks ─────────────────────────────────────────────────────────────
  "Усі": "All",
  "Усі проєкти": "All projects",
  "Готово": "Done",
  "Заплановано": "Planned",
  "Чекають вас": "Waiting for you",
  "Чекають вашої відповіді": "Waiting for your answer",
  "Усього задач": "Tasks in total",
  "задача": "task",
  "задач": "tasks",
  "Задач з таким статусом немає.": "No tasks with this status.",
  "Не вдалося завантажити задачі": "Could not load the tasks",
  "Завантаження…": "Loading…",
  "Готується": "In preparation",
  "Ваша відповідь": "Your answer",

  // ── payments ──────────────────────────────────────────────────────────
  "Оплата": "Payment",
  "До оплати": "To pay",
  "Оплачено": "Paid",
  "Оплачено повністю": "Paid in full",
  "Уже оплачено": "Already paid",
  "Уже сплачено": "Already paid",
  "Прострочено": "Overdue",
  "Ще не виставлено": "Not invoiced yet",
  "Залишилось сплатити": "Left to pay",
  "Вартість проєкту": "Project value",
  "Оплатити зараз": "Pay now",
  "Оплатити в строк": "Pay on time",
  "Оплатити до": "Pay by",
  "Пізніше": "Later",
  "пізніше": "later",
  "Після здачі робіт": "After delivery",
  "після здачі": "after delivery",
  "Нічого": "Nothing",
  "платити поки не потрібно": "nothing to pay for now",
  "Зараз нічого оплачувати не потрібно": "Nothing to pay right now",
  "Тут поки порожньо": "Nothing here yet",
  "Не вдалося завантажити рахунки": "Could not load the invoices",
  "Передоплата": "Prepayment",
  "Проміжний": "Interim",
  "Фінальний": "Final",
  "Повна оплата": "Full payment",
  "платіж": "payment",
  "платежі": "payments",
  "платежів": "payments",
  "Перший": "First",
  "Другий": "Second",
  "Третій": "Third",
  "Четвертий": "Fourth",
  "П’ятий": "Fifth",
  "Передоплата — з неї ми запускаємо роботу.": "Prepayment — this is what starts the work.",
  "Проміжний платіж — після проміжного етапу робіт.": "Interim payment — after a milestone of the work.",
  "Фінальний платіж — після здачі робіт і вашого погодження.":
    "Final payment — after delivery and your approval.",
  "Повна оплата — уся вартість послуги одним платежем, без поділу на частини.":
    "Full payment — the whole service in one payment, no parts.",
  "Уся вартість послуги — одним платежем.": "The whole service in a single payment.",
  "Кожна послуга може бути розділена на частини: передоплата запускає роботу, фінальний платіж — після здачі.":
    "A service can be split into parts: the prepayment starts the work, the final payment follows delivery.",
  "Оплата карткою — лише за посиланням від менеджера.":
    "Card payment only through a link from your manager.",
  "Оплата карткою — лише якщо менеджер надіслав посилання (іконка картки).":
    "Card payment only if your manager sent a link (the card icon).",
  "Оплата — переказом за реквізитами з рахунку за договором; у рядку видно, хто платник і хто отримувач.":
    "Payment by bank transfer using the details on the contract invoice; the row shows the payer and the recipient.",
  "Оплата — переказом за реквізитами з рахунку, виставленого за договором (іконка рахунку в рядку).":
    "Payment by bank transfer using the details on the contract invoice (the invoice icon in the row).",
  "Оплатити карткою (посилання від менеджера)": "Pay by card (link from your manager)",
  "Рахунок можна завантажити будь-коли; у ньому вказано, до якої дати його оплатити.":
    "You can download the invoice at any time; it states the date it should be paid by.",
  "Рахунок (ще не надіслано — можна завантажити заздалегідь)":
    "Invoice (not sent yet — you can download it in advance)",
  "Рахунок ще не надіслано — зараз платити не потрібно.":
    "The invoice has not been sent yet — nothing to pay now.",
  "Після оплати тут з’являється акт виконаних робіт.":
    "Once paid, the certificate of completion appears here.",
  "Акт виконаних робіт": "Certificate of completion",
  "Завантажити рахунок": "Download invoice",

  // ── documents / penalty ───────────────────────────────────────────────
  "Рахунок": "Invoice",
  "Акт": "Act",
  "Рахунок завантажено": "Invoice downloaded",
  "Акт завантажено": "Act downloaded",
  "Завантаження рахунку…": "Downloading the invoice…",
  "Завантаження акта…": "Downloading the act…",
  "Не вдалося завантажити документ": "Could not download the document",
  "Рахунок ще готує менеджер — реквізити для цього платежу не заповнені":
    "Your manager is still preparing the invoice — the details for this payment are not filled in",
  "Пеня": "Late fee",
  "Нараховується пеня": "A late fee is accruing",
  "ще не нараховується": "not accruing yet",
  "Пільговий період": "Grace period",
  "Перші": "First",
  "після дати оплати — пільгові, без пені.": "after the due date are free of any late fee.",
  "щодня": "per day",
  "Розрахунок": "Calculation",
  "Чому така сума": "Why this amount",
  "Сума": "Amount",
  "Згорнути": "Collapse",
  "Пояснення": "Explanation",
  "договору": "of the contract",
  "суми договору": "of the contract value",
  "суми рахунку": "of the invoice amount",
  "рахунок": "invoice",
  "рахунки": "invoices",
  "рахунків": "invoices",
  "рахунку": "invoice",
  "не вказано": "not specified",
  "план": "plan",

  // ── project passport ──────────────────────────────────────────────────
  "Договір": "Contract",
  "Підписано": "Signed",
  "Проєкт здано": "Project delivered",
  "здача сьогодні": "delivery today",
  "З нашого боку": "On our side",
  "З вашого боку": "On your side",
  "контактна особа": "contact person",
  "менеджер проєкту": "project manager",
  "менеджер з продажу": "sales manager",
  "доступ до кабінету": "cabinet access",
  "Контакт не вказано": "No contact specified",
  "Менеджера призначимо найближчим часом": "A manager will be assigned shortly",
  "Ваш головний контакт: хід робіт, правки, терміни":
    "Your main contact: progress, revisions, deadlines",
  "Договір, рахунки, нові задачі": "Contract, invoices, new tasks",
  "Погоджує роботи й отримує рахунки": "Approves the work and receives the invoices",
  "Скільки часу у вас є, щоб відповісти на наш запит чи погодити роботу.":
    "How long you have to answer a request or approve the work.",
  "Запланована дата здачі минула — актуальний строк уточнює ваш менеджер проєкту.":
    "The planned delivery date has passed — your project manager will confirm the new one.",

  // ── change requests ───────────────────────────────────────────────────
  "Зміна обсягу робіт": "Change of scope",
  "Зміна бюджету": "Change of budget",
  "Зміна дедлайну": "Change of deadline",
  "Поточний бюджет:": "Current budget:",
  "Новий бюджет:": "New budget:",
  "Поточний дедлайн:": "Current deadline:",
  "Новий дедлайн:": "New deadline:",
  "Поточний:": "Current:",
  "Новий:": "New:",
  "Різниця:": "Difference:",
  "Бюджет:": "Budget:",
  "Дедлайн:": "Deadline:",
  "Причина зміни:": "Reason for the change:",
  "Додайте коментар до вашого рішення...": "Add a comment to your decision…",
  "Зміни схвалено": "Change approved",
  "Зміни відхилено": "Change rejected",
  "Не вдалося обробити запит": "Could not process the request",
  "Не вказано": "Not specified",
  "Режим перегляду — дія недоступна": "Preview mode — action unavailable",
  "в проєкті": "in the project",
  "до": "by",
  "завтра": "tomorrow",
  "через": "in",
  "прострочено на": "overdue by",
  "Етап задачі": "Task stage",

  // ── service catalogue shown to the client ─────────────────────────────
  "Спін-хаус 360°": "360° spin-house",
  "Інтерактивний обліт будинку: покупець обертає модель і обирає квартиру прямо на фасаді.":
    "An interactive fly-around: the buyer rotates the building and picks an apartment right on the facade.",
  "Рендери інтер’єрів": "Interior renders",
  "Фотореалістичні зображення квартир і місць загального користування.":
    "Photorealistic images of the apartments and common areas.",
  "Рендери екстер’єру": "Exterior renders",
  "Фотореалістичні зображення будинку для сайту, реклами та відділу продажу.":
    "Photorealistic images of the building for the website, ads and the sales office.",
  "Віртуальні тури: клієнт «заходить» у квартиру чи двір і роздивляється все навколо.":
    "Virtual tours: the client steps into an apartment or the yard and looks around.",
  "Точна 3D-модель об’єкта — основа для рендерів, турів і спін-хаусу.":
    "A precise 3D model of the property — the basis for renders, tours and the spin-house.",
  "Планування": "Floor plans",
  "2D/3D-планування квартир для сайту та відділу продажу.":
    "2D/3D apartment plans for the website and the sales office.",
  "Анімація": "Animation",
  "Відеоролик про проєкт для сайту, реклами та презентацій.":
    "A video about the project for the website, ads and presentations.",
  "Дизайн сайту": "Website design",
  "Унікальний дизайн сайту у вашому фірмовому стилі.":
    "A unique website design in your brand style.",
  "Сайт проєкту: дизайн, верстка та адмін-панель для оновлення цін і наявності.":
    "The project website: design, build and an admin panel for prices and availability.",

  // ── task stages ───────────────────────────────────────────────────────
  "Моделювання": "Modelling",
  "Текстурування": "Texturing",
  "Рендеринг": "Rendering",
  "Постобробка": "Post-production",
  "Дизайн": "Design",
  "Фронтенд": "Frontend",
  "Бекенд": "Backend",
  "Тестування": "Testing",
  "Перевірка": "Review",
  "Погодження": "Approval",
  "Передача": "Handover",
  "Правки": "Revisions",
  "Уточнення": "Clarification",
  "Призначено": "Assigned",
  "Відкрито": "Open",
  "Погоджено": "Approved",
  "Передано": "Handed over",
  "Здано": "Delivered",
  "Сплачено": "Paid",
  "Ваша перевірка": "Your review",
  "Ваші правки": "Your revisions",
  "Чекає на вашу відповідь": "Waiting for your answer",
  "Задачі ще не створені": "No tasks created yet",
  "Бриф": "Brief",
  "Матеріали": "Materials",
  "Чат проєкту": "Project chat",
  "Архів": "Archive",
  "Показати": "Show",
  "Сховати": "Hide",
  "Переглянути": "View",
  "Відповісти": "Reply",
  "Що ми робимо для вас": "What we do for you",

  // ── money / totals ────────────────────────────────────────────────────
  "Залишок": "Remaining",
  "Більше нічого платити не потрібно": "Nothing left to pay",
  "Проєкт сплачено повністю": "The project is paid in full",
  "Прострочена оплата": "Overdue payment",
  "Рахунок до оплати": "Invoice to pay",
  "Рахунок надішлемо, коли настане час": "We will send the invoice when it is due",
  "Рахунок ще готує менеджер": "Your manager is still preparing the invoice",
  "Нараховано пеню": "Late fee accrued",
  "Як рахується": "How it is calculated",
  "Після здачі проєкту": "After the project is delivered",
  "Суми у валютах договору": "Amounts in the contract currencies",
  "гривнях": "hryvnia",
  "доларах США": "US dollars",
  "євро": "euro",
  "грн": "UAH",
  "послуга": "service",
  "послуги": "services",
  "послуг": "services",
  "Актуальний строк уточнює ваш менеджер": "Your manager will confirm the current date",
  "Строк уточнюється": "Date to be confirmed",
  "завершений": "completed",
  "завершені": "completed",
  "завершених": "completed",
  "завершений проєкт": "completed project",
  "завершені проєкти": "completed projects",
  "завершених проєктів": "completed projects",

  // ── short month names (dates) ─────────────────────────────────────────
  "січ": "Jan", "лют": "Feb", "бер": "Mar", "кві": "Apr", "тра": "May", "чер": "Jun",
  "лип": "Jul", "сер": "Aug", "вер": "Sep", "жов": "Oct", "лис": "Nov", "гру": "Dec",
  "січня": "January", "лютого": "February", "березня": "March", "квітня": "April",
  "травня": "May", "червня": "June", "липня": "July", "серпня": "August",
  "вересня": "September", "жовтня": "October", "листопада": "November", "грудня": "December",

  // ── payments, penalties, passport (second pass) ───────────────────────
  "Вартість поділено на": "The cost is split into",
  "Ваш коментар (необов'язково)": "Your comment (optional)",
  "Відкрити проєкт": "Open project",
  "Відхилити": "Reject",
  "До": "By",
  "За договором, якщо рахунок не оплачено до дати, вказаної в ньому, нараховується пеня —": "Under the contract, an invoice not paid by the date stated on it accrues a late-payment penalty —",
  "Запит на зміни": "Change request",
  "Оновити": "Refresh",
  "Оплатіть рахунок — нарахування зупиниться. Якщо вважаєте, що пеню нараховано помилково, напишіть вашому менеджеру проєкту.": "Pay the invoice and the penalty stops. If you believe it was charged by mistake, write to your project manager.",
  "Орієнтовно": "Around",
  "Отримувач (наша юрособа)": "Payee (our legal entity)",
  "Пеня рахується в доларах США (суму рахунку перераховано за курсом) і зупиняється в день оплати.": "The penalty is calculated in US dollars (the invoice amount is converted at the exchange rate) and stops on the day of payment.",
  "Платник": "Payer",
  "Рахунки та оплата": "Invoices and payment",
  "Рахунок надіслано": "Invoice sent",
  "Схвалити": "Approve",
  "Треба було оплатити до": "Was due by",
  "Що і коли оплачувати": "What to pay and when",
  "Якщо оплата затримується": "If payment is late",
  "більше ніж на": "by more than",
  "від": "of",
  "год": "h",
  "далі": "next",
  "дн.": "d",
  "з": "of",
  "з оплатою": "with payments due",
  "за день від суми": "per day of the",
  "за день.": "per day.",
  "за кожен день прострочення": "for every day overdue",
  "залишилось": "left",
  "найближчий до": "nearest by",
  "пеня": "penalty",
  "пеня з’явиться через": "penalty starts in",
  "почнеться з": "starts in",
  "прострочено": "overdue",
  "пільговий період — пеня з": "grace period — penalty from",
  "пільгових": "grace",
  "після дати в рахунку — пеня": "after the invoice date — penalty of",
  "після цієї дати, нараховується пеня": "after that date, a penalty of",
  "ще": "another",

  // ── client pages: portal, projects, requests (second pass) ────────────
  "Інформацію надано": "Information provided",
  "Без проєкту": "No project",
  "Бриф проєкту": "Project brief",
  "Будь-які додаткові примітки...": "Any additional notes...",
  "Бюджет": "Budget",
  "Вартість": "Cost",
  "Ваш відгук": "Your feedback",
  "Ваш відгук:": "Your feedback:",
  "Ваш відгук: ": "Your feedback: ",
  "Ваш коментар": "Your comment",
  "Ваш коментар (необовʼязково)": "Your comment (optional)",
  "Ваш коментар до відповіді": "Your comment on the reply",
  "Ваша надіслана відповідь": "The reply you sent",
  "Ваші надіслані дані": "The details you sent",
  "Введіть": "Enter",
  "Введіть вашу відповідь...": "Type your reply...",
  "Версія": "Version",
  "Виконавця призначено": "Assignee appointed",
  "Виконано": "Done",
  "Вносимо правки": "Making revisions",
  "Вставте посилання (Google Drive, Dropbox...)": "Paste a link (Google Drive, Dropbox...)",
  "Від вас": "From you",
  "Від вас зараз нічого не потрібно": "Nothing is needed from you right now",
  "Відповідь надіслано": "Reply sent",
  "Відповідь надіслано!": "Reply sent!",
  "Відповіли": "Replied",
  "Відповісти до": "Reply by",
  "Відповісти на запит": "Reply to the request",
  "Відхилити зміни": "Reject the changes",
  "Готуємо до старту": "Preparing to start",
  "Дата відповіді": "Reply date",
  "Дедлайн": "Deadline",
  "Деталі запиту": "Request details",
  "До моїх проєктів": "To my projects",
  "Додайте відгук (для погодження — необовʼязково, для правок — бажано)...": "Add feedback (optional to approve, helpful for revisions)...",
  "Додайте відгук або примітки...": "Add feedback or notes...",
  "Додайте примітки за потреби...": "Add notes if needed...",
  "Додатковий коментар (необовʼязково)": "Additional comment (optional)",
  "Додаткових деталей немає.": "No further details.",
  "Додаткові коментарі (необовʼязково)": "Additional comments (optional)",
  "Додаткові примітки:": "Additional notes:",
  "Етап": "Stage",
  "Завантажити ваш файл": "Download your file",
  "Завантажити файл": "Download file",
  "Завантажте файл або вставте посилання...": "Upload a file or paste a link...",
  "Задача": "Task",
  "Задачі зʼявляться тут, щойно почнеться робота.": "Tasks will appear here as soon as work begins.",
  "Закрити": "Close",
  "Залишилось": "Left",
  "Запит від": "Request from",
  "Запит на правки надіслано": "Revision request sent",
  "Запит не знайдено": "Request not found",
  "Запитано правки": "Revisions requested",
  "Запити": "Requests",
  "Запити від нашої команди": "Requests from our team",
  "Заповнено": "Filled in",
  "Заповнити": "Fill in",
  "Заповнити запит": "Fill in the request",
  "Заповніть необхідну інформацію": "Fill in the information we need",
  "Заповніть обовʼязкові поля": "Please fill in the required fields",
  "Заповніть поле": "Please fill in",
  "Заповніть, будь ласка": "Please fill in",
  "Зміни": "Changes",
  "Зміни схвалено!": "Changes approved!",
  "Менеджер": "Manager",
  "Мої проєкти": "My projects",
  "На внутрішній перевірці": "In internal review",
  "Надіслана інформація": "Information sent",
  "Надіслано": "Sent",
  "Надіслано вам": "Sent to you",
  "Надіслати відповідь": "Send reply",
  "Надіслати інформацію": "Send information",
  "Назад": "Back",
  "Назад до огляду": "Back to overview",
  "Не вдалося завантажити деталі запиту": "Couldn't load the request details",
  "Не вдалося завантажити запит": "Couldn't load the request",
  "Не вдалося завантажити запити": "Couldn't load the requests",
  "Не вдалося завантажити кабінет": "Couldn't load your portal",
  "Не вдалося завантажити проєкт": "Couldn't load the project",
  "Не вдалося завантажити проєкти": "Couldn't load the projects",
  "Не вдалося надіслати відгук": "Couldn't send your feedback",
  "Не вдалося надіслати відповідь": "Couldn't send your reply",
  "Ні": "No",
  "Оберіть": "Choose",
  "Оберіть...": "Choose...",
  "Обовʼязково": "Required",
  "Окремих полів не запитували": "No specific fields were requested",
  "Опис запиту": "Request description",
  "Оплатити": "Pay",
  "Оплатити карткою": "Pay by card",
  "Очікує": "Pending",
  "Очікує вашого перегляду": "Waiting for your review",
  "Очікує на вас": "Waiting on you",
  "Очікує на відповідь": "Waiting for a reply",
  "Очікує оплати": "Awaiting payment",
  "ПРОСТРОЧЕНО": "OVERDUE",
  "Перегляд кабінету клієнта": "Viewing the client portal",
  "Перегляд роботи": "Reviewing the work",
  "Переглядайте та відстежуйте свої проєкти.": "View and track your projects.",
  "Повна оплата 100%": "Full payment 100%",
  "Погодження обсягу": "Scope approval",
  "Погодження робіт": "Work approval",
  "Погодити": "Approve",
  "Погодити зміни": "Approve the changes",
  "Погодити роботу": "Approve the work",
  "Позначте, якщо застосовно": "Tick if it applies",
  "Поки немає — покажемо результат тут, щойно він буде готовий.": "Nothing yet — the result will show up here as soon as it's ready.",
  "Полів для заповнення": "Fields to fill in",
  "Потрібна дія": "Action needed",
  "Потрібна інформація": "Information needed",
  "Потрібне погодження": "Approval needed",
  "Потрібно заповнити": "Needs filling in",
  "Потрібні зміни": "Changes needed",
  "Потрібні правки": "Revisions needed",
  "Почати заповнення": "Start filling in",
  "Пошук проєктів...": "Search projects...",
  "Призупинено": "On hold",
  "Пропозиція надіслана": "Proposal sent",
  "Прострочено · було до": "Overdue · was due by",
  "Проєкт не знайдено": "Project not found",
  "Проєктів поки немає": "No projects yet",
  "Результати": "Results",
  "Референсні файли": "Reference files",
  "Роботу готово — перегляньте результат і погодьте або опишіть правки.": "The work is ready — review it and either approve it or describe the revisions.",
  "Роботу погоджено": "Work approved",
  "Роботу погоджено!": "Work approved!",
  "Роботу погоджено! Нарахування пені зупинено.": "Work approved! The penalty has stopped accruing.",
  "Скільки кіл правок входить у вартість. Понад ліміт — за окремою домовленістю.": "How many rounds of revisions are included in the price. Beyond that limit, by separate agreement.",
  "Статус": "Status",
  "Створено": "Created",
  "Строк": "Due",
  "Так": "Yes",
  "Так, підтверджую": "Yes, I confirm",
  "Термін": "Due",
  "Треба було відповісти до": "A reply was due by",
  "Узгодження договору": "Contract negotiation",
  "Усього": "Total",
  "Уточнюється": "To be confirmed",
  "Файл": "File",
  "Хід робіт": "Progress",
  "Цей запит прострочено!": "This request is overdue!",
  "Цю задачу завершено": "This task is finished",
  "Чекає вашого погодження": "Waiting for your approval",
  "Чернетка": "Draft",
  "Що змінити (необов’язково при погодженні)": "What to change (optional when approving)",
  "Що ми робимо": "What we're doing",
  "Що потрібно від вас:": "What we need from you:",
  "Що робимо": "What we're doing",
  "Щойно ми почнемо роботу, ваші проєкти зʼявляться тут.": "Once we start work, your projects will appear here.",
  "безкоштовних": "free",
  "верс.": "ver.",
  "версія": "version",
  "вкажіть коректне посилання": "please enter a valid link",
  "вкажіть коректний email": "please enter a valid email",
  "готово": "done",
  "до оплати": "to pay",
  "запит": "request",
  "запити": "requests",
  "запитів": "requests",
  "зміни в задачі": "changes to the task",
  "на погодження": "to approve",
  "режим лише для перегляду": "read-only mode",
  "✓ Відповіли": "✓ Replied",
  "✓ Так": "✓ Yes",
  "✗ Ні": "✗ No",
  "задач готово": "tasks done",
  "Договір від": "Contract of",
  "К": "C",
  "Оплата прострочена на": "Payment overdue by",
  "Пеня почне нараховуватись через": "The penalty starts accruing in",
  "Погодьте": "Please approve",
  "Погодьте зміну для": "Please approve the change to",
  "Потрібна ваша відповідь до": "Your reply is needed by",
  "Після цього продовжимо роботу.": "Then we'll carry on with the work.",
  "Суми в": "Amounts in",
  "Термін оплати був": "It was due on",
  "без нього ми не можемо стартувати.": "without it we can't start.",
  "бер": "Mar",
  "березня": "March",
  "вер": "Sep",
  "гру": "Dec",
  "грудня": "December",
  "до здачі": "to delivery",
  "жов": "Oct",
  "жовтня": "October",
  "здано": "delivered",
  "кві": "Apr",
  "квітня": "April",
  "липня": "July",
  "лис": "Nov",
  "листопада": "November",
  "лют": "Feb",
  "лютого": "February",
  "сер": "Aug",
  "серпня": "August",
  "тра": "May",
  "чер": "Jun",
  "червня": "June",
  "як у договорі": "as in the contract",
  "термін був": "was due on",
  "На цьому проєкті від вас нічого не потрібно": "Nothing is needed from you on this project",
  "на інших проєктах": "on other projects",
  "Перейти": "Go to",
  "запит чекає": "request is waiting",
  "запити чекають": "requests are waiting",
  "запитів чекають": "requests are waiting",
  "Приблизно в": "Approximately in",
  "за курсом НБУ": "at the NBU rate",
  "Матеріали від вас": "Materials from you",
  "отримано": "received",
  "Отримано": "Received",
  "Ще не отримано": "Not received yet",
  "Відкрийте послугу, щоб побачити, які матеріали для неї потрібні від вас. Позначені «Не надано» надсилайте в чат проєкту або менеджеру.":
    "Open a service to see which materials we need from you for it. Send the ones marked “Not provided” to the project chat or your manager.",
  "надано": "provided",
  "Надано": "Provided",
  "Не надано": "Not provided",
  "не надано": "not provided",
  "Потрібно надати": "Still needed",
  "Розгорнути все": "Expand all",
  "Згорнути все": "Collapse all",
  "Усе отримали": "All received",
  "Потрібно ще": "Still needed:",
  "Цей самий матеріал потрібен і для інших послуг": "The same material is needed for other services too",
  "додано менеджером": "added by your manager",
  "Усе з цього списку ви вже надали.": "You have provided everything on this list.",
  "Поки нічого не отримали.": "Nothing received yet.",
  "Від вас для цієї послуги нічого не потрібно.": "Nothing is needed from you for this service.",
  "Перелік матеріалів для цієї послуги готує ваш менеджер — щойно він буде готовий, ви побачите його тут.": "Your manager is preparing the list of materials for this service — you will see it here as soon as it is ready.",
  "360° Відеосфера": "360° Video Sphere",
  "Сферичне відео, зняте на рівні очей: «оживляє» локацію та оточення проєкту.": "A spherical video shot at eye level that brings the location and its surroundings to life.",
  "Не знаєте, що надіслати? Напишіть менеджеру, підкажемо.": "Not sure what to send? Message your manager and we'll help.",
  "Надіслати файли в чат": "Send files to the chat",
  "Надішліть матеріали для роботи": "Send us the materials for the work",
  "Щоб ми не зупиняли роботу, надайте ще": "So the work doesn't stall, please provide",
  "матеріал": "material",
  "матеріали": "materials",
  "матеріалів": "materials",
  "і ще": "and",
  "Показати, що потрібно": "Show what's needed",
  "Надіслати в чат": "Send to chat",
};

const DICTS = { uk: {}, en: EN };

export const translate = (lang, key) => {
  if (!key || typeof key !== "string") return key;
  const dict = DICTS[lang];
  if (!dict) return key;
  return dict[key] ?? key;
};

const LocaleContext = createContext("uk");

// Module-level mirror of the active language. Date and money formatters are
// plain functions outside the React tree (and one component names its task
// prop `t`), so they read the language from here instead of the hook.
let CURRENT_LANG = "uk";
export const currentLang = () => CURRENT_LANG;
/** Translate outside a component — same dictionary, ambient language. */
export const tr = (key) => translate(CURRENT_LANG, key);

export const CabinetLocaleProvider = ({ lang, children }) => {
  const value = DICTS[lang] ? lang : "uk";
  CURRENT_LANG = value;
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

/** `const { t, lang } = useCabinetLang()` — t("Огляд") → "Overview" in English. */
export const useCabinetLang = () => {
  const lang = useContext(LocaleContext);
  return useMemo(() => ({ lang, t: (key) => translate(lang, key) }), [lang]);
};

/** Plural picker that stays correct in both languages: Ukrainian needs three
 *  forms, English two — so English just ignores the third. */
export const pluralFor = (lang, n, forms) => {
  if (lang === "en") return Math.abs(n) === 1 ? translate(lang, forms[0]) : translate(lang, forms[1]);
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};

export default useCabinetLang;
